/**
 * Example data put into a fresh database. Each seed runs once per database (remembered in the meta collection), so
 * deleting the examples does not bring them back, and a later build can add a new seed that runs on update.
 */
import { foldText, listExercises, newExerciseDraft, type ExerciseDraft } from "./exercises";
import { META, newId, type Backend } from "./storage";
import type { Template } from "./templates";

export interface Seed {
  id: string;
  run(db: Backend): Promise<void>;
}

const SEED_EXERCISES: ExerciseDraft[] = [
  newExerciseDraft({ name: "Sandbag Clean", equipment: "sandbag", bodyPart: "fullBody", measure: "timed", restSec: 20 }),
  newExerciseDraft({ name: "Sandbag Squat", equipment: "sandbag", bodyPart: "legs", measure: "reps", restSec: 90 }),
  newExerciseDraft({ name: "Kettlebell Swing", equipment: "kettlebell", bodyPart: "fullBody", measure: "timed", restSec: 30 }),
  newExerciseDraft({ name: "Push-up", equipment: "bodyweight", bodyPart: "chest", measure: "reps", restSec: 60 }),
  newExerciseDraft({ name: "Plank", equipment: "bodyweight", bodyPart: "core", measure: "hold", restSec: 60 }),
  newExerciseDraft({ name: "Sled Push", equipment: "sled", bodyPart: "legs", measure: "distance", restSec: 120 }),
];

/**
 * "Sandbag AMRAP": Sandbag Clean, 8 × 40 s, 20 s rest. Seed exercises have random ids, so Sandbag Clean is found
 * by name (a visible one first); if the owner deleted or renamed it, it is created again.
 */
async function seedSandbagAmrap(db: Backend) {
  const draft = SEED_EXERCISES[0];
  const all = (await listExercises(db)).filter((e) => foldText(e.name) === foldText(draft.name));
  let clean = all.find((e) => !e.hidden) ?? all[0];
  if (!clean) {
    clean = { ...draft, id: newId() };
    await db.put("exercises", clean);
  }
  const template: Template = {
    id: newId(),
    name: "Sandbag AMRAP",
    notes: "",
    exercises: [{ exerciseId: clean.id, restSec: 20, sets: Array.from({ length: 8 }, () => ({ type: "normal", durationSec: 40 })) }],
  };
  await db.put("templates", template);
}

export const SEEDS: Seed[] = [
  {
    id: "exercises-1",
    run: async (db) => {
      for (const d of SEED_EXERCISES) await db.put("exercises", { ...d, id: newId() });
    },
  },
  { id: "templates-1", run: seedSandbagAmrap },
];

type SeedRecord = { id: "seeds"; done: string[] };

/** Runs the seeds this database has not had yet. */
export async function applySeeds(db: Backend, seeds: Seed[] = SEEDS): Promise<void> {
  const done = new Set((await db.get<SeedRecord>(META, "seeds"))?.done ?? []);
  for (const s of seeds) {
    if (done.has(s.id)) continue;
    await s.run(db);
    done.add(s.id);
    const record: SeedRecord = { id: "seeds", done: [...done] };
    await db.put(META, record);
  }
}
